import { ApolloClientOptions } from '../../lib/apollo-client';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import isEmail from 'sane-email-validation';

/**
 * Netcore's Smartech script saves user info as an encrypted cookie even if the user is signed out.
 * This `smartech` function on the window allows us to fetch that cookie (which they call token),
 * decrypt it, and access the previously or currently signed in user's email via the `ck` field.
 */

declare global {
  interface Window {
    smartech: SmartTechAPI | unknown;
  }
}

type SmartTechAPI = (
  method: 'get',
  value: 'token'
) => Promise<SmartechTokenInterface>;

interface SmartechTokenInterface {
  uuid?: string;
  ck?: string; //email address
  token?: string;
}

/*
 * This typeguard is the closest I can get to checking whether the `smartech` function is
 * the correct type. At least we can check that it is a function and that it accepts two string arguments.
 */
const isSmartTechAPI = (smartech: unknown): smartech is SmartTechAPI => {
  return typeof smartech === 'function';
};

const isSmartechTokenInterfaceWithEmail = (
  token: unknown | SmartechTokenInterface
): token is SmartechTokenInterface => {
  return (
    typeof token === 'object' &&
    token !== null &&
    'ck' in token &&
    typeof (token as SmartechTokenInterface).ck === 'string' &&
    isEmail((token as SmartechTokenInterface).ck)
  );
};

const SmartechTokenLoader = ({
  setClientOptions,
}: {
  setClientOptions: Dispatch<SetStateAction<ApolloClientOptions>>;
}) => {
  const [retries, setRetries] = useState(0);
  const hasSetClientOptions = useRef(false);

  useEffect(() => {
    if (retries >= 3 || hasSetClientOptions.current) return;
    const setSmartechTokenAsync = async () => {
      try {
        if (window && isSmartTechAPI(window.smartech)) {
          const token = await window.smartech('get', 'token');
          if (isSmartechTokenInterfaceWithEmail(token)) {
            setClientOptions(options => ({
              ...options,
              sessionEmail: token.ck,
            }));
            hasSetClientOptions.current = true;
            return;
          }
        }
        if (retries < 3) {
          setRetries(r => r + 1);
          setTimeout(setSmartechTokenAsync, 1000);
        }
      } catch {
        if (retries < 3) {
          setRetries(r => r + 1);
          setTimeout(setSmartechTokenAsync, 1000);
        }
      }
    };

    setSmartechTokenAsync();
  }, [retries, setClientOptions]);
  return null;
};

export default SmartechTokenLoader;
